import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { fetchData, fetchJson } from '../../api/fetchJson'
import { orgPath } from '../../config/orgId'
import {
  Button,
  Card,
  Currency,
  Empty,
  ErrorBanner,
  Field,
  FormattedDate,
  Input,
  Loading,
  PageHeader,
  Select,
  Textarea,
} from '../../components/ui'
import { flagIfAuthError } from '../../components/ResetBanner'

type ExpenseEntry = {
  id: string
  amount?: number
  type?: string | null
  category_name?: string | null
  description?: string | null
  date?: number | null
  agency_name?: string | null
  staff_name?: string | null
  payment_method?: string | null
}

type ExpenseCategory = {
  id: string
  name: string
}

type ExpenseType = 'general' | 'other' | 'labour' | 'salary'

const PAYMENT_METHODS: Array<{ value: string; label: string }> = [
  { value: 'cash', label: 'Cash' },
  { value: 'account', label: 'Bank account' },
]

export function ExpensesPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const qc = useQueryClient()

  const list = useQuery<ExpenseEntry[]>({
    queryKey: ['expenses', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      fetchData<ExpenseEntry[]>(orgPath(`/projects/${projectId}/accounts/expenses`)),
  })

  const categories = useQuery<ExpenseCategory[]>({
    queryKey: ['expense-categories'],
    queryFn: () => fetchData<ExpenseCategory[]>(orgPath('/accounts/expense-categories')),
    staleTime: 60_000,
  })

  const [amount, setAmount] = useState('')
  const [type, setType] = useState<ExpenseType>('general')
  const [paymentMethod, setPaymentMethod] = useState('cash')
  const [categoryId, setCategoryId] = useState('')
  const [description, setDescription] = useState('')
  const [paidTo, setPaidTo] = useState('')

  const create = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        type,
        amount: Number(amount),
        payment_method: paymentMethod,
        date: Math.floor(Date.now() / 1000),
      }
      if (description) body.description = description
      if (categoryId) body.category_id = categoryId
      if (type === 'labour' && paidTo) body.agency_name = paidTo
      if (type === 'salary' && paidTo) body.staff_name = paidTo
      return fetchJson(orgPath(`/projects/${projectId}/accounts/expenses`), {
        method: 'POST',
        body: JSON.stringify(body),
      })
    },
    onError: flagIfAuthError,
    onSuccess: () => {
      setAmount('')
      setDescription('')
      setPaidTo('')
      setCategoryId('')
      qc.invalidateQueries({ queryKey: ['expenses', projectId] })
    },
  })

  if (list.isLoading) return <Loading />
  if (list.isError) return <ErrorBanner error={list.error} />

  return (
    <div className="space-y-6">
      <PageHeader title="Project expenses" />

      <Card>
        <h2 className="font-semibold mb-3">New expense</h2>
        <form
          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            create.mutate()
          }}
        >
          <Field label="Amount">
            <Input
              type="number"
              min={0}
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </Field>
          <Field label="Type">
            <Select
              value={type}
              onChange={(e) => setType(e.target.value as ExpenseType)}
            >
              <option value="general">General</option>
              <option value="other">Other</option>
              <option value="labour">Labour</option>
              <option value="salary">Salary</option>
            </Select>
          </Field>
          <Field label="Payment method">
            <Select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
              {PAYMENT_METHODS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Category (optional)">
            <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">—</option>
              {(categories.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label={
              type === 'labour'
                ? 'Agency'
                : type === 'salary'
                  ? 'Staff name'
                  : 'Paid to (optional)'
            }
          >
            <Input
              value={paidTo}
              onChange={(e) => setPaidTo(e.target.value)}
              required={type === 'labour' || type === 'salary'}
            />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Description (optional)">
              <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
            </Field>
          </div>
          {create.isError && (
            <div className="sm:col-span-2">
              <ErrorBanner error={create.error} />
            </div>
          )}
          <div className="sm:col-span-2 flex justify-end">
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Saving…' : 'Save expense'}
            </Button>
          </div>
        </form>
      </Card>

      {!list.data || list.data.length === 0 ? (
        <Empty message="No expenses yet." />
      ) : (
        <Card>
          <ul className="divide-y divide-surface-700 text-sm">
            {list.data.map((e) => {
              const paidTo = e.agency_name ?? e.staff_name ?? null
              return (
                <li key={e.id} className="py-2 flex justify-between gap-3">
                  <div>
                    <div className="font-medium">
                      <Currency amount={e.amount ?? null} />
                      {e.category_name && (
                        <span className="text-xs text-surface-400 ml-2">
                          {e.category_name}
                        </span>
                      )}
                      {e.type && (
                        <span className="text-xs text-surface-500 ml-2">[{e.type}]</span>
                      )}
                    </div>
                    {e.description && (
                      <div className="text-xs text-surface-400 mt-0.5">{e.description}</div>
                    )}
                    {paidTo && <div className="text-xs text-surface-400">To: {paidTo}</div>}
                  </div>
                  <div className="text-xs text-surface-400 text-right">
                    <FormattedDate ts={e.date ?? null} />
                  </div>
                </li>
              )
            })}
          </ul>
        </Card>
      )}
    </div>
  )
}
